import React, { ReactNode, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { AxiosResponse } from 'axios';

import GenericModal from '~/components/GenericModal/GenericModal';
import classes from '../BuildingBulkImport/BuildingBulkImport.module.css';

const MAX_ROWS = 500;

export type CsvImportRow = Record<string, string>;

export type CsvImportStatus =
  | 'create'
  | 'created'
  | 'skip'
  | 'invalid'
  | 'failed';

export type CsvImportResult = {
  row: number;
  status: CsvImportStatus;
  reason: string | null;
};

export type CsvImportResponse<R extends CsvImportResult> = {
  dryRun: boolean;
  summary: {
    total: number;
    toCreate: number;
    created: number;
    failed: number;
    skipped: number;
    invalid?: number;
  };
  results: R[];
};

const STATUS_LABEL: Record<CsvImportStatus, string> = {
  create: '✅ Will create',
  created: '✅ Created',
  skip: '⏭ Skipped',
  invalid: '❌ Invalid',
  failed: '❌ Failed'
};

const headerKey = (h: string) =>
  h
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

async function readRows(file: File): Promise<CsvImportRow[]> {
  const workbook = XLSX.read(await file.arrayBuffer(), {
    type: 'array',
    raw: true
  });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    raw: false,
    defval: ''
  });
  return rows
    .map((r) =>
      Object.fromEntries(
        Object.entries(r).map(([k, v]) => [headerKey(k), String(v).trim()])
      )
    )
    .filter((r) => Object.values(r).some(Boolean));
}

function downloadCsv(content: string, filename: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type Props<R extends CsvImportResult> = {
  title: string;
  intro: ReactNode;
  requiredColumns: string[];
  templateColumns: string[];
  templateRow: string;
  templateFilename: string;
  resultsFilename: string;
  // Table and results-CSV columns, besides row/status/reason.
  columns: { header: string; value: (r: R) => string }[];
  importRows: (
    rows: CsvImportRow[],
    dryRun: boolean
  ) => Promise<AxiosResponse<CsvImportResponse<R>>>;
  onClose: (changedAny: boolean) => void;
};

function CsvImportModal<R extends CsvImportResult>({
  title,
  intro,
  requiredColumns,
  templateColumns,
  templateRow,
  templateFilename,
  resultsFilename,
  columns,
  importRows,
  onClose
}: Props<R>) {
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<CsvImportRow[]>([]);
  const [response, setResponse] = useState<CsvImportResponse<R> | null>(null);
  const [busy, setBusy] = useState(false);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDone = response && !response.dryRun;
  const toSave = response?.summary.toCreate ?? 0;
  const saved = response?.summary.created ?? 0;
  const hasInvalid = (response?.summary.invalid ?? 0) > 0;

  const pickFile = (f: File | null) => {
    if (busy || !f) return;
    setFile(f);
    setRows([]);
    setResponse(null);
    setFileError('');
  };

  const checkFile = async () => {
    if (!file) return;
    setFileError('');
    let parsed: CsvImportRow[];
    try {
      parsed = await readRows(file);
    } catch {
      setFileError('Could not read this file. Upload a .csv or Excel file.');
      return;
    }
    const missing = requiredColumns.filter(
      (c) => !parsed.length || !(c in parsed[0])
    );
    if (!parsed.length) return setFileError('The file has no rows.');
    if (missing.length)
      return setFileError(`Missing columns: ${missing.join(', ')}`);
    if (parsed.length > MAX_ROWS)
      return setFileError(
        `The file has ${parsed.length} rows. Upload at most ${MAX_ROWS} at a time.`
      );

    setBusy(true);
    try {
      const { data } = await importRows(parsed, true);
      setRows(parsed);
      setResponse(data);
    } catch {
      // API client shows the toast
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await importRows(rows, false);
      setResponse(data);
      toast(`${data.summary.created} rows saved`);
    } catch {
      // Re-validate to show rows that became invalid since the preview.
      try {
        setResponse((await importRows(rows, true)).data);
      } catch {
        // API client shows the toast
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadResults = () => {
    if (!response) return;
    const sheet = XLSX.utils.json_to_sheet(
      response.results.map((r) => ({
        row: r.row,
        ...Object.fromEntries(columns.map((c) => [c.header, c.value(r)])),
        status: STATUS_LABEL[r.status],
        reason: r.reason || ''
      }))
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Results');
    XLSX.writeFile(book, resultsFilename, { bookType: 'csv' });
  };

  return (
    <GenericModal
      showModal
      title={title}
      width='960px'
      enableOverflowScrolls
      dismissOnTapOutside={false}
      cancelOverride='Close'
      onChange={() => {
        if (!busy) onClose(saved > 0);
      }}
      actionText={busy && response ? 'Importing…' : 'Import'}
      actionFunction={save}
      actionButtonDisabled={
        busy || !response || !!isDone || hasInvalid || toSave === 0
      }
      content={
        <div>
          <p className={classes.intro}>{intro}</p>
          <input
            ref={fileInputRef}
            type='file'
            accept='.csv,.xlsx,.xls'
            style={{ display: 'none' }}
            onChange={(e) => {
              pickFile(e.target.files?.[0] || null);
              // Lets the same (edited) file fire onChange when picked again.
              e.target.value = '';
            }}
          />
          <div className={classes.uploadRow}>
            <div
              role='button'
              tabIndex={0}
              className={`${classes.dropZone} ${
                busy ? classes.dropZoneDisabled : ''
              }`}
              onClick={() => !busy && fileInputRef.current?.click()}
              onKeyDown={(e) =>
                e.key === 'Enter' && !busy && fileInputRef.current?.click()
              }
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                pickFile(e.dataTransfer.files?.[0] || null);
              }}
            >
              {file ? (
                <>
                  <span className={classes.fileName}>📄 {file.name}</span>
                  <span className={classes.hint}>Click to choose another</span>
                </>
              ) : (
                <>
                  <span className={classes.fileName}>
                    Drop your CSV here or click to choose
                  </span>
                  <span className={classes.hint}>
                    Then press Validate. Import unlocks once every row passes.
                  </span>
                </>
              )}
            </div>
            <button
              type='button'
              className='btn-standard btn-primary'
              disabled={!file || busy}
              onClick={checkFile}
            >
              {busy && !response ? 'Validating…' : 'Validate'}
            </button>
          </div>
          {fileError && <span className={classes.error}>{fileError}</span>}
          <div className={classes.links}>
            <a
              href='#'
              className={classes.link}
              onClick={(e) => {
                e.preventDefault();
                downloadCsv(
                  `${templateColumns.join(',')}\n${templateRow}\n`,
                  templateFilename
                );
              }}
            >
              Download template
            </a>
            {isDone && (
              <a
                href='#'
                className={classes.link}
                onClick={(e) => {
                  e.preventDefault();
                  downloadResults();
                }}
              >
                Download results CSV
              </a>
            )}
          </div>
          {response && (
            <div className={classes.section}>
              <div className={classes.stats}>
                <span>Total: {response.summary.total}</span>
                {isDone ? (
                  <>
                    <span>Created: {response.summary.created}</span>
                    <span>Failed: {response.summary.failed}</span>
                  </>
                ) : (
                  <span>Will create: {response.summary.toCreate}</span>
                )}
                <span>Skipped: {response.summary.skipped}</span>
                {response.summary.invalid !== undefined && (
                  <span>Invalid: {response.summary.invalid}</span>
                )}
              </div>
              {hasInvalid && (
                <span className={classes.error}>
                  Fix the CSV and validate again. Import needs no invalid rows.
                </span>
              )}
              <div className={classes.tableWrap}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th>Sheet row</th>
                      {columns.map((c) => (
                        <th key={c.header}>{c.header}</th>
                      ))}
                      <th>Status</th>
                      <th>Problems</th>
                    </tr>
                  </thead>
                  <tbody>
                    {response.results.map((r) => (
                      <tr key={r.row}>
                        <td>{r.row}</td>
                        {columns.map((c) => (
                          <td key={c.header}>{c.value(r)}</td>
                        ))}
                        <td>{STATUS_LABEL[r.status]}</td>
                        <td>{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      }
    />
  );
}

export default CsvImportModal;
